import { useCallback, useEffect, useRef, useState } from "react";
import { FlaskConical, Trash2 } from "lucide-react";
import AuthAsset from "./AuthAsset";
import { useConfirm } from "@/components/confirm-dialog";
import { StatusBadge, type Tone } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  promptTestsApi,
  promptTestImageUrl,
  type PromptTestBookOption,
  type PromptTestOptions,
  type PromptTestRun,
} from "../lib/prompt-tests-api";

const STATE_LABEL: Record<PromptTestRun["state"], string> = {
  pending: "wartet",
  running: "läuft",
  succeeded: "fertig",
  failed: "fehlgeschlagen",
};

const RUN_TONE: Record<PromptTestRun["state"], Tone> = {
  pending: "neutral",
  running: "info",
  succeeded: "success",
  failed: "critical",
};

const PLACEHOLDER =
  "flex aspect-square w-full items-center justify-center bg-muted text-xs text-muted-foreground";

function isActive(run: PromptTestRun): boolean {
  return run.state === "pending" || run.state === "running";
}

function seconds(ms: number | null): string {
  return ms == null ? "—" : `${(ms / 1000).toFixed(1)} s`;
}

/**
 * `/v1/images/edits` rendert nur Größen, deren beide Kanten durch 16 teilbar
 * sind. Der Server rastet ohnehin ein — hier wird es nur schon beim Tippen
 * sichtbar, damit niemand rätselt, warum aus 440 plötzlich 432 wird.
 */
function snap16(value: number): number {
  if (!Number.isFinite(value)) return 256;
  return Math.min(4096, Math.max(256, Math.round(value / 16) * 16));
}

/** Abweichung des Testverhältnisses vom Verhältnis der Bestellung, in Prozent. */
function ratioDrift(
  test: { width: number; height: number },
  orderSize: string,
): number | null {
  const m = /^(\d{3,5})x(\d{3,5})$/.exec(orderSize);
  if (!m) return null;
  const target = Number(m[1]) / Number(m[2]);
  return (test.width / test.height / target - 1) * 100;
}

/**
 * Ein Bild eines Laufs. Der Cache-Buster hängt am Zustand, damit das fertige
 * Ergebnis nachgeladen wird, sobald der Lauf durch ist — die URL selbst
 * ändert sich ja nicht.
 */
function RunImage({
  run,
  kind,
  caption,
}: {
  run: PromptTestRun;
  kind: "sheet" | "scene";
  caption: string;
}) {
  const ready = kind === "sheet" ? run.sheetPath : run.scenePath;
  return (
    <figure className="m-0 overflow-hidden rounded-lg border bg-card">
      {ready ? (
        <AuthAsset
          url={`${promptTestImageUrl(run.id, kind)}?v=${run.finishedAt ?? run.state}`}
          alt={caption}
          placeholder="—"
          className="w-full bg-muted object-contain"
          placeholderClassName={PLACEHOLDER}
        />
      ) : (
        <div className={PLACEHOLDER}>{isActive(run) ? "läuft…" : "—"}</div>
      )}
      <figcaption className="px-2 py-1.5 text-xs text-muted-foreground">
        {caption}
      </figcaption>
    </figure>
  );
}

function PromptBlock({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  if (!value) return null;
  return (
    <details className="group rounded-md border px-2 py-1.5">
      <summary className="cursor-pointer text-xs font-medium">
        {label}{" "}
        <span className="font-normal text-muted-foreground">
          · {value.length} Zeichen
        </span>
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto font-mono text-xs whitespace-pre-wrap">
        {value}
      </pre>
    </details>
  );
}

/**
 * „Diese Bestellung, aber nur der Anfang und in klein."
 *
 * Startet an einem Upload denselben Ablauf, den eine echte Bestellung nimmt —
 * Character-Sheet aus dem Original-Foto, Outfit-/Identity-Extraktion, EINE
 * Szenen-Platzierung — mit identischen Prompts und Modell-Parametern, nur in
 * niedriger Auflösung. Damit lassen sich Prompts durchprobieren, ohne auf eine
 * bezahlte Bestellung zu warten oder Druckauflösung zu bezahlen.
 */
export default function PromptTestPanel({
  uniqid,
  options,
}: {
  uniqid: string;
  options: PromptTestOptions | null;
}) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<PromptTestRun[] | null>(null);
  const [bookId, setBookId] = useState<string>("");
  const [sceneIndex, setSceneIndex] = useState<number | null>(null);
  // null = „noch nicht angefasst", dann gilt der Vorschlag der gewählten Szene.
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [reuseSheet, setReuseSheet] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hochzählen startet die Poll-Schleife neu — nötig, wenn ein neuer Lauf
  // beginnt, während gerade keiner mehr lief.
  const [pollKey, setPollKey] = useState(0);
  const timer = useRef<number | null>(null);

  const book: PromptTestBookOption | undefined =
    options?.books.find((b) => b.id === bookId) ?? options?.books[0];
  const scene =
    book?.scenes.find((s) => s.index === sceneIndex) ?? book?.scenes[0];

  useEffect(() => {
    if (options && !bookId && options.books[0]) setBookId(options.books[0].id);
  }, [options, bookId]);

  const size = {
    width: width ?? scene?.suggested_width ?? 1024,
    height: height ?? scene?.suggested_height ?? 1024,
  };
  const drift = scene ? ratioDrift(size, scene.order_size) : null;

  const refresh = useCallback(async () => {
    const data = await promptTestsApi.list(uniqid);
    setRuns(data.runs);
    return data.runs;
  }, [uniqid]);

  // Solange ein Lauf offen ist, alle 5 s nachfragen. Ein Sheet braucht Minuten,
  // deshalb pollt das Panel statt auf eine Antwort zu warten.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const tick = async (): Promise<void> => {
      try {
        const list = await refresh();
        if (cancelled) return;
        if (list.some(isActive)) {
          timer.current = window.setTimeout(tick, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
        // Sonst bliebe „Lädt…" für immer stehen und würde den Fehler darunter
        // wie einen Zwischenstand aussehen lassen.
        setRuns((prev) => prev ?? []);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [open, refresh, pollKey]);

  async function start(): Promise<void> {
    setStarting(true);
    setError(null);
    try {
      const run = await promptTestsApi.create({
        uniqid,
        bookId: book?.id,
        sceneIndex: scene?.index,
        sceneWidth: snap16(size.width),
        sceneHeight: snap16(size.height),
        reuseSheet,
      });
      setRuns((prev) => [run, ...(prev ?? [])]);
      setPollKey((k) => k + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStarting(false);
    }
  }

  async function remove(id: string): Promise<void> {
    const ok = await confirm({
      title: "Testlauf löschen?",
      description: "Der Testlauf wird samt seinen Bildern gelöscht.",
      confirmLabel: "Testlauf löschen",
      destructive: true,
    });
    if (!ok) return;
    try {
      await promptTestsApi.remove(id);
      setRuns((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          <FlaskConical />
          Prompt-Test öffnen
        </Button>
        <span className="text-xs text-muted-foreground">
          Character-Sheet + erste Szene wie bei einer Bestellung, nur klein
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">Prompt-Test</h3>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Schließen
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor={`${uniqid}-book`}>Buch</FieldLabel>
          <Select
            value={book?.id ?? ""}
            onValueChange={(v) => {
              setBookId(v);
              setSceneIndex(null);
            }}
          >
            <SelectTrigger id={`${uniqid}-book`} className="w-full bg-card">
              <SelectValue placeholder="Buch wählen" />
            </SelectTrigger>
            <SelectContent>
              {(options?.books ?? []).map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor={`${uniqid}-scene`}>Szene</FieldLabel>
          <Select
            value={scene ? String(scene.index) : ""}
            onValueChange={(v) => setSceneIndex(Number(v))}
          >
            <SelectTrigger id={`${uniqid}-scene`} className="w-full bg-card">
              <SelectValue placeholder="Szene wählen" />
            </SelectTrigger>
            <SelectContent>
              {(book?.scenes ?? []).map((s) => (
                <SelectItem key={s.index} value={String(s.index)}>
                  {s.name}
                  {s.enabled ? "" : " (inaktiv)"}
                  {s.emotion ? ` — ${s.emotion}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor={`${uniqid}-w`}>Breite (px)</FieldLabel>
          <Input
            id={`${uniqid}-w`}
            type="number"
            min={256}
            max={4096}
            step={16}
            className="bg-card"
            value={size.width}
            onChange={(e) => setWidth(Number(e.target.value))}
            onBlur={(e) => setWidth(snap16(Number(e.target.value)))}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor={`${uniqid}-h`}>Höhe (px)</FieldLabel>
          <Input
            id={`${uniqid}-h`}
            type="number"
            min={256}
            max={4096}
            step={16}
            className="bg-card"
            value={size.height}
            onChange={(e) => setHeight(Number(e.target.value))}
            onBlur={(e) => setHeight(snap16(Number(e.target.value)))}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Field orientation="horizontal" className="w-auto">
          <Checkbox
            id={`${uniqid}-reuse`}
            checked={reuseSheet}
            onCheckedChange={(v) => setReuseSheet(v === true)}
          />
          <FieldLabel htmlFor={`${uniqid}-reuse`}>
            Sheet des letzten Laufs wiederverwenden
          </FieldLabel>
        </Field>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setWidth(null);
            setHeight(null);
          }}
          disabled={width === null && height === null}
          title="Zurück auf die beste 16er-Näherung an das Buch-Verhältnis"
        >
          Größe zurücksetzen
        </Button>
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => void start()}
          disabled={starting || !scene}
        >
          {starting ? "Startet…" : "Testlauf starten"}
        </Button>
      </div>

      {scene && (
        <p className="text-xs text-muted-foreground">
          Szene läuft über <strong>{scene.provider}</strong>. Bestellung rendert{" "}
          <code>{scene.order_size}</code>, dieser Test{" "}
          <code>
            {snap16(size.width)}×{snap16(size.height)}
          </code>
          {drift != null && (
            <>
              {" "}
              ({drift >= 0 ? "+" : ""}
              {drift.toFixed(2)} % Seitenverhältnis)
            </>
          )}
          . Prompts, Modell und Qualität sind identisch, nur die Auflösung sinkt
          (beide Kanten durch 16 teilbar). Ohne Wiederverwendung wird das Sheet
          in voller Qualität neu gerendert und dauert einige Minuten.
        </p>
      )}

      {error && <p className="text-destructive">{error}</p>}

      {!runs ? (
        <p className="text-muted-foreground">Lädt…</p>
      ) : runs.length === 0 ? (
        <p className="text-muted-foreground">
          Noch kein Testlauf für diesen Upload.
        </p>
      ) : (
        <ul className="space-y-3">
          {runs.map((run) => (
            <li key={run.id} className="space-y-3 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={RUN_TONE[run.state]}>
                  {STATE_LABEL[run.state]}
                </StatusBadge>
                <strong>Szene {run.sceneIndex}</strong>
                <span className="text-xs text-muted-foreground">
                  {run.sceneWidth}×{run.sceneHeight} · Sheet{" "}
                  {run.sheetReused ? "wiederverwendet" : seconds(run.sheetMs)} ·
                  Szene {seconds(run.sceneMs)}
                </span>
                <time className="ml-auto text-xs text-muted-foreground">
                  {new Date(run.createdAt).toLocaleString("de-DE")}
                </time>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => void remove(run.id)}
                  aria-label="Testlauf löschen"
                  title="Testlauf löschen"
                >
                  <Trash2 />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <RunImage run={run} kind="sheet" caption="Character-Sheet" />
                <RunImage
                  run={run}
                  kind="scene"
                  caption={`Szene ${run.sceneIndex}`}
                />
              </div>

              {run.error && <p className="text-destructive">{run.error}</p>}

              <div className="space-y-1">
                <PromptBlock label="Sheet-Prompt" value={run.sheetPrompt} />
                <PromptBlock label="Szenen-Prompt" value={run.scenePrompt} />
                <PromptBlock
                  label="outfit_description"
                  value={run.outfitDescription}
                />
                <PromptBlock label="char_identity" value={run.charIdentity} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
